import React from "react";
import { Box, Button, Checkbox, FormControlLabel, Paper, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import { ROUTES } from "../routes";
import { getAuthToken } from "../utils/authSession";

const TOUR_VERSION = "v2";

const steps = [
  {
    title: "Welcome to Susha ITAM",
    body: "This quick tour will show you the key areas of your workspace.",
    target: null,
  },
  {
    title: "Workspace Navigation",
    body: "Use this side menu to move between dashboard, assets, monitor, cloud, and CMDB.",
    target: '[data-tour="workspace-nav"]',
  },
  {
    title: "Dashboard",
    body: "This is your high-level enterprise view with asset, device, software, and cloud signals.",
    target: '[data-tour="nav-dashboard"]',
  },
  {
    title: "Assets",
    body: "Manage inventory lifecycle, ownership, warranty, and assignment from Assets.",
    target: '[data-tour="nav-assets"]',
  },
  {
    title: "Monitor",
    body: "Track device health, discovered software, and operational activity in Monitor.",
    target: '[data-tour="nav-monitor"]',
  },
  {
    title: "Cloud Connections",
    body: "Connect AWS, Azure, and Microsoft 365 here to pull cloud-side visibility.",
    target: '[data-tour="nav-cloud"]',
  },
  {
    title: "Account",
    body: "Open your account menu for profile settings, password change, and logout.",
    target: '[data-tour="top-account"]',
  },
  {
    title: "Overview Cards",
    body: "These cards show total assets and online/offline device status at a glance.",
    target: '[data-tour="dashboard-stats"]',
  },
  {
    title: "Cloud Priority",
    body: "Use this section to quickly see which cloud integrations are healthy or need action.",
    target: '[data-tour="dashboard-cloud-priority"]',
  },
];

function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}

export default function AppTour({ role, enterpriseId }) {
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const [stepIndex, setStepIndex] = React.useState(0);
  const [rect, setRect] = React.useState(null);
  const [dontShowAgain, setDontShowAgain] = React.useState(false);

  const optOutKey = React.useMemo(
    () => `susha_app_tour_optout_${TOUR_VERSION}_${enterpriseId || "na"}`,
    [enterpriseId]
  );
  const sessionSeenKey = React.useMemo(() => {
    const token = getAuthToken() || "";
    const stamp = token ? token.slice(-16) : "na";
    return `susha_app_tour_seen_${TOUR_VERSION}_${enterpriseId || "na"}_${stamp}`;
  }, [enterpriseId]);

  const currentStep = steps[stepIndex];

  React.useEffect(() => {
    if (role !== "enterprise_admin") return;
    if (location.pathname !== ROUTES.ENTERPRISE) return;
    const optedOut = localStorage.getItem(optOutKey) === "done";
    const seenThisLogin = sessionStorage.getItem(sessionSeenKey) === "done";
    if (!optedOut && !seenThisLogin) {
      setOpen(true);
      sessionStorage.setItem(sessionSeenKey, "done");
    }
  }, [location.pathname, optOutKey, role, sessionSeenKey]);

  React.useEffect(() => {
    if (!open) return;
    const selector = currentStep?.target;
    if (!selector) {
      setRect(null);
      return;
    }

    const update = () => {
      const el = document.querySelector(selector);
      if (!el) {
        setRect(null);
        return;
      }
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      const r = el.getBoundingClientRect();
      setRect(r);
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [currentStep, open]);

  const finishTour = () => {
    if (dontShowAgain) localStorage.setItem(optOutKey, "done");
    setOpen(false);
  };

  const next = () => {
    if (stepIndex >= steps.length - 1) {
      finishTour();
      return;
    }
    setStepIndex(i => i + 1);
  };

  const back = () => setStepIndex(i => Math.max(0, i - 1));

  if (!open) return null;

  const tooltipWidth = 340;
  const padding = 14;
  let tooltipTop = window.innerHeight * 0.5 - 120;
  let tooltipLeft = window.innerWidth * 0.5 - tooltipWidth * 0.5;

  if (rect) {
    const preferredTop = rect.bottom + 12;
    const canFitBottom = preferredTop + 220 < window.innerHeight;
    tooltipTop = canFitBottom ? preferredTop : Math.max(12, rect.top - 230);
    tooltipLeft = clamp(rect.left, 12, window.innerWidth - tooltipWidth - 12);
  }

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1800,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "rgba(2, 6, 23, 0.62)",
        }}
      />

      {rect && (
        <Box
          sx={{
            position: "absolute",
            top: rect.top - padding,
            left: rect.left - padding,
            width: rect.width + padding * 2,
            height: rect.height + padding * 2,
            borderRadius: 2,
            border: "2px solid #60a5fa",
            boxShadow: "0 0 0 9999px rgba(2, 6, 23, 0.35)",
            pointerEvents: "none",
          }}
        />
      )}

      <Paper
        elevation={6}
        sx={{
          position: "absolute",
          width: tooltipWidth,
          maxWidth: "calc(100vw - 24px)",
          top: clamp(tooltipTop, 12, window.innerHeight - 260),
          left: clamp(tooltipLeft, 12, window.innerWidth - tooltipWidth - 12),
          borderRadius: 2,
          p: 2,
          border: "1px solid #dbe6f3",
        }}
      >
        <Typography variant="overline" sx={{ color: "#64748b", fontWeight: 700 }}>
          Step {stepIndex + 1} of {steps.length}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a", mb: 0.5 }}>
          {currentStep.title}
        </Typography>
        <Typography variant="body2" sx={{ color: "#475569", mb: 2 }}>
          {currentStep.body}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              Don&apos;t show me again
            </Typography>
          }
          sx={{ mb: 1 }}
        />

        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Button onClick={finishTour} sx={{ textTransform: "none", color: "#475569" }}>
            Skip
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={back}
              disabled={stepIndex === 0}
              variant="outlined"
              sx={{ textTransform: "none" }}
            >
              Back
            </Button>
            <Button onClick={next} variant="contained" sx={{ textTransform: "none" }}>
              {stepIndex === steps.length - 1 ? "Finish" : "Next"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
